import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formsApi } from '../api/formsApi';
import type { FormDetail, FormVersion, FormioSchema } from '../api/types';
import { FormSettingsPanel, settingsFromForm, type FormSettings } from './FormSettingsPanel';
import { AuditPanel } from './AuditPanel';
import { EmailPanel } from './EmailPanel';
import { EmbedPanel } from './EmbedPanel';
import { RespondentLink } from './RespondentLink';
import { SecretLinksPanel } from './SecretLinksPanel';
import { WebhookPanel } from './WebhookPanel';

export function FormEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<FormDetail | null>(null);
  const [schema, setSchema] = useState<FormioSchema>({});
  const [settings, setSettings] = useState<FormSettings | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const applyForm = useCallback((loaded: FormDetail) => {
    setForm(loaded);
    setSettings(settingsFromForm(loaded));
    // Keep the current draft/published schema so settings saves don't clobber the fields.
    setSchema((loaded.draft ?? loaded.published)?.schema ?? {});
  }, []);

  useEffect(() => {
    if (!id) {
      return;
    }
    formsApi
      .get(id)
      .then(applyForm)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load form'));
  }, [id, applyForm]);

  const hasDraft = !!form?.draft;

  function saveBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function exportVersion(version: FormVersion) {
    if (!form) {
      return;
    }
    setError(null);
    try {
      saveBlob(await formsApi.exportVersion(form.id, version.id), `${form.title}-v${version.versionNumber}.json`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    }
  }

  async function run(action: () => Promise<FormDetail>, message: string) {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const updated = await action();
      applyForm(updated);
      setStatus(message);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Operation failed');
    } finally {
      setBusy(false);
    }
  }

  if (!form || !settings) {
    return (
      <div className="page">
        {error ? <p className="error">{error}</p> : <p>Loading…</p>}
      </div>
    );
  }

  // Persist the current settings panel (title/access mode/brand/flags) to the draft.
  // Fields are edited in the Form Builder; the schema is sent back unchanged.
  const persistSettings = () =>
    formsApi.updateDraft(form.id, {
      schema,
      title: settings.title,
      accessMode: settings.accessMode,
      theme: { primaryColour: settings.primaryColour },
      settings: {
        containsSensitiveInfo: settings.containsSensitiveInfo,
        allowSaveAndResume: settings.allowSaveAndResume,
      },
    });

  return (
    <div className="page">
      <div className="editor-header">
        <h1>{form.title}</h1>
        <div className="actions">
          <button
            disabled={busy || !hasDraft}
            onClick={() => run(persistSettings, 'Settings saved.')}
          >
            Save settings
          </button>
          <button
            disabled={busy || !hasDraft}
            onClick={() =>
              run(async () => {
                // Persist any unsaved settings (e.g. an access-mode change) before publishing,
                // so publishing reflects what's on screen rather than the last saved state.
                await persistSettings();
                return formsApi.publish(form.id);
              }, 'Published.')
            }
          >
            Publish
          </button>
          <button
            disabled={busy || hasDraft || !form.publishedVersionId}
            onClick={() => run(() => formsApi.createDraft(form.id), 'New draft created.')}
          >
            New draft
          </button>
          {(form.draft ?? form.published) && (
            <button disabled={busy} onClick={() => exportVersion((form.draft ?? form.published)!)}>
              Download JSON
            </button>
          )}
        </div>
      </div>

      {!hasDraft && (
        <p className="note">
          This form is published (v{form.published?.versionNumber}) and read-only. Create a new draft to edit.
        </p>
      )}
      {status && <p className="success">{status}</p>}
      {error && <p className="error">{error}</p>}

      <div className="editor-layout">
        <FormSettingsPanel settings={settings} disabled={busy || !hasDraft} onChange={setSettings} />
      </div>

      {form.accessMode === 'SecretLink' ? (
        <div className="editor-section">
          <SecretLinksPanel formId={form.id} />
        </div>
      ) : (
        <div className="editor-section">
          <RespondentLink
            formId={form.id}
            accessMode={form.accessMode}
            published={!!form.publishedVersionId}
          />
        </div>
      )}

      <div className="editor-section">
        <EmailPanel formId={form.id} />
      </div>

      <div className="editor-section">
        <WebhookPanel formId={form.id} />
      </div>

      <div className="editor-section">
        <EmbedPanel formId={form.id} />
      </div>

      <div className="editor-section">
        <AuditPanel formId={form.id} />
      </div>
    </div>
  );
}
